import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';
import type { WhatsAppMessage } from './useWhatsAppMessages';

/**
 * One WhatsApp conversation thread (slice 70c).
 *
 * The thread is addressed by its opaque `conversationId` (the server encodes
 * `(tenant|null, number)`; the client never parses it). Messages come newest-first,
 * keyset-paginated — the SCREEN reverses to show oldest at the top and pages OLDER
 * on "load earlier". The window `{open, expiresAt}` is server-computed and the same
 * on every page; the composer and countdown read it. The full number is on the wire
 * only for a null-tenant ("Unknown sender") thread.
 */

export interface ConversationMessage extends WhatsAppMessage {
  /** 'template' | 'text' on an outbound row; null on inbound. */
  kind: string | null;
  /** The staff member who typed a text reply; null for inbound / template rows. */
  sentByName: string | null;
}

export interface ConversationWindow {
  open: boolean;
  /** ISO of the free-reply deadline (latest inbound + 24 h), or null if no inbound. */
  expiresAt: string | null;
}

export interface ConversationPage {
  conversationId: string;
  tenantId: string | null;
  tenantName: string | null;
  /** Full E.164 number, ONLY for a null-tenant thread; null otherwise. */
  peerPhone: string | null;
  messages: ConversationMessage[];
  nextCursor: string | null;
  window: ConversationWindow;
}

export const conversationQueryKey = (conversationId: string) =>
  ['admin', 'whatsapp', 'conversation', conversationId] as const;

export function useConversation(
  conversationId: string,
): UseInfiniteQueryResult<{ pages: ConversationPage[]; pageParams: unknown[] }, Error> {
  return useInfiniteQuery({
    queryKey: conversationQueryKey(conversationId),
    queryFn: ({ pageParam, signal }) => {
      const p = new URLSearchParams();
      if (typeof pageParam === 'string' && pageParam !== '') p.set('cursor', pageParam);
      const qs = p.toString();
      return apiJson<ConversationPage>(
        `/api/admin/whatsapp/conversations/${encodeURIComponent(conversationId)}${qs ? `?${qs}` : ''}`,
        { signal },
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // A 4xx (a non-super-admin gets 404; a bad id 404) is a decision, not a hiccup.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}
